/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ImageWithPreload } from '@coursera-web/cds-labs';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import { Button as CDSButton, Typography, Typography2, breakpoints, typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon, ExternalLinkIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockHowQuery,
  EntWebsiteHowFragment,
  EntWebsiteProofPointFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import BenefitList from 'bundles/ent-website/components/BenefitList';
import EpxandedContentColumns from 'bundles/ent-website/components/EpxandedContentColumns';
import type { ButtonVariants } from 'bundles/ent-website/contentful/core/BlockLayout';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import Image from 'bundles/ent-website/contentful/core/Image';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import RichText from 'bundles/ent-website/contentful/core/RichText';
import Video from 'bundles/ent-website/contentful/core/Video';
import { ExternalEmbedWidget } from 'bundles/ent-website/contentful/modules/ExternalEmbedWidget';
import InlineProofPoints from 'bundles/ent-website/contentful/modules/InlineProofPoints';
import ProofPoints from 'bundles/ent-website/contentful/modules/ProofPoints';
import { TestimonialModule } from 'bundles/ent-website/contentful/modules/TestimonialModule';
import { entWebsiteBlockHowQuery } from 'bundles/ent-website/queries/EntWebsiteHow';
import type { LayoutOptions, NonNullableArray } from 'bundles/ent-website/types';
import DataConsumerWrapper from 'bundles/ent-website/utils/DataConsumerWrapper';
import parseLayoutOptions from 'bundles/ent-website/utils/parseLayoutOptions';
import {
  getPromoCodeSubstMap,
  runSubstituitonsForRichText,
  runSubstituitonsForString,
} from 'bundles/ent-website/utils/promoCodeUtils';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

type HowModuleProps = {
  preLoadHeroImage?: boolean;
};

export type Props = BlockModuleInnerProps<EntWebsiteHowFragment> & HowModuleProps;

const styles = {
  eyebrow: (_: Theme, eyebrowColor: string) => css`
    font-size: 20px !important;
    line-height: 150%;
    letter-spacing: -0.03em;
    font-weight: 600;
    text-transform: uppercase;
    color: ${eyebrowColor ?? 'var(--cds-color-white-0)'};
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('xs')} {
      font-size: 14px !important;
    }
  `,

  headline: () => css`
    font-size: 48px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: 100% !important;
    letter-spacing: -0.03em;
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('xs')} {
      font-size: 40px !important;
    }
  `,

  headlineHero: css`
    font-size: 64px !important;
  `,

  description: css`
    li {
      font-size: 20px !important;
      line-height: 150%;
      letter-spacing: -0.02em;

      ${breakpoints.down('xs')} {
        font-size: 16px !important;
        letter-spacing: -0.03em;
      }
    }

    h1,
    h2,
    h3 {
      letter-spacing: -0.03em;
      font-weight: 600;
    }

    h1 {
      font-size: 4rem;
      line-height: 4.5rem;
      margin-bottom: 28px;
    }

    h2 {
      font-size: 2.75rem;
      line-height: 3.25rem;
      margin-bottom: 24px;
    }

    h3 {
      font-size: 2rem;
      line-height: 2.5rem;
      margin-bottom: 20px;
    }

    p {
      ${typography2.bodyPrimary}
      font-size: 20px !important;
      line-height: 150% !important;
      letter-spacing: -0.02em;

      ${breakpoints.down('xs')} {
        font-size: 16px !important;
        letter-spacing: -0.03em;
      }
    }

    .rc-Markdown.styled li {
      font-size: 20px !important;
    }
  `,

  descriptionHero: css`
    p {
      font-size: 24px !important;

      ${breakpoints.down('xs')} {
        font-size: 18px !important;
        letter-spacing: -0.03em;
      }
    }
  `,

  subCtaText: () => css`
    margin-top: var(--cds-spacing-300);

    p {
      ${typography2.bodyPrimary};
      margin-bottom: 0;

      ${breakpoints.down('xs')} {
        ${typography2.bodySecondary};
      }
    }
  `,

  externalLinkIcon: () => css`
    margin-left: var(--cds-spacing-100);
  `,

  howDetailsTwoColumns: () => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding-left: 0;

    li {
      list-style-type: none;
      flex-basis: 50%;
      padding-right: var(--cds-spacing-600);
      padding-bottom: var(--cds-spacing-300);

      ${breakpoints.down('sm')} {
        flex: 1 1 100%;
        padding-right: 0;
      }
    }
  `,

  howDetailsTwoColumnsHeadline: css`
    font-size: 1rem !important;
  `,

  howDetailsTwoColumnsDesc: css`
    /* stylelint-disable-line no-empty-source */
  `,

  ctaSection: () => css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-400);
    }
  `,
  partnerList: (_: Theme, options: LayoutOptions) => css`
    overflow: hidden;
    ${options.innerLayout && `max-width: 406px;`}

    ul {
      list-style-type: none;
      position: relative;
      padding-inline-start: 0;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      margin-top: 12px;
      margin-bottom: 0;
      left: 1px;
      top: 1px;
    }

    ul li {
      overflow: hidden;
      height: 98px;
      display: flex;
      padding: 8px 24px;
      align-items: center;
      justify-content: center;
      border-right: 1px solid var(--cds-color-grey-50);
      border-bottom: 1px solid var(--cds-color-grey-50);

      ${breakpoints.down('xs')} {
        max-height: 55px;
        padding: 8px;
      }
    }

    ul li img {
      max-width: 100%;
      object-fit: cover;
      max-height: 100%;
      ${breakpoints.down('xs')} {
        max-height: 48px;
      }
    }
  `,
  partnerImage: css`
    width: auto;
  `,

  ctaSectionHero: () => css`
    margin-top: var(--cds-spacing-800);
    margin-bottom: var(--cds-spacing-800);
  `,
  imageWrapper: () => css`
    width: 100%;
    height: 100%;

    img {
      border-radius: var(--cds-spacing-100);
    }
  `,
  image: css`
    max-height: 100% !important;
    object-fit: cover;
    object-position: top;
    height: 100% !important;
    width: 100%;
  `,
  ghostAlignment: css`
    margin-left: -16px;
  `,

  secondaryCTAs: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    margin-left: -16px;
  `,
};

export function HowModule({ content, assembly = {}, preLoadHeroImage }: Props) {
  const { visibility, eyebrow, eyebrowColor, ctaVariant } = assembly;
  const location = useLocation();

  const promoCodeSubstMap: Record<string, string> = React.useMemo(
    () =>
      getPromoCodeSubstMap(content?.promoCodeConfigsCollection?.items, simplifyQueryValue(location?.query?.promoCode)),
    [content?.promoCodeConfigsCollection?.items, location?.query?.promoCode]
  );

  const options = parseLayoutOptions(assembly.layoutOptions);

  const theme = useTheme();

  const { headline, description, ctaText, subCtaText, subCtaTextMarkdown } = React.useMemo(
    () => ({
      headline: runSubstituitonsForString(content?.headline, promoCodeSubstMap),
      description: runSubstituitonsForString(content?.description, promoCodeSubstMap),
      ctaText: runSubstituitonsForString(content?.ctaText, promoCodeSubstMap),
      subCtaText: {
        ...content?.subCtaText,
        json: runSubstituitonsForRichText(content?.subCtaText?.json, promoCodeSubstMap),
      },
      subCtaTextMarkdown: runSubstituitonsForString(content?.subCtaTextMarkdown, promoCodeSubstMap),
    }),
    [
      content?.ctaText,
      content?.description,
      content?.headline,
      content?.subCtaText,
      content?.subCtaTextMarkdown,
      promoCodeSubstMap,
    ]
  );

  if (!content) return null;

  const {
    image,
    video,
    ceros,
    ctaTarget,
    howDetailsCollection,
    testimonialCollection,
    proofPointsCollection,
    proofPointsModuleHeading,
    proofPointsModuleSubheading,
    proofPointsModuleBackgroundColor,
    proofPointsModuleFontColor,
    proofPointsModuleCta,
    proofPointsModuleCtaButtonVariant,
    secondaryCtAsCollection,
    associatedPartnersCollection,
  } = content;

  // TODO: `assembly.layout === 'hero' is deprecated, verify that it is not used in contentful then remove it
  const isHero = assembly.layout === 'hero' || options.hero;
  const imageEl = ceros ? (
    <ExternalEmbedWidget assembly={{}} content={ceros} noBlockLayout />
  ) : (
    <>
      {image && video && <Video image={image} video={video} />}
      {image && !video && (
        <div css={styles.imageWrapper}>
          {preLoadHeroImage ? (
            <ImageWithPreload
              shouldPreload={isHero}
              shouldLazyLoad={!isHero}
              css={styles.image}
              {...image}
              width={image.width ?? undefined}
            />
          ) : (
            <Image css={styles.image} content={image} width={image.width ?? undefined} isBackground={isHero} />
          )}
        </div>
      )}
    </>
  );

  const associatedPartnersEl =
    visibility?.associatedPartners && associatedPartnersCollection && associatedPartnersCollection.items.length > 0 ? (
      <div css={styles.partnerList(theme, options)}>
        <ul>
          {associatedPartnersCollection.items.map((partner) =>
            partner?.logo ? (
              <li key={partner.name}>
                {preLoadHeroImage ? (
                  <Image content={partner.logo} width="auto" />
                ) : (
                  <ImageWithPreload {...partner.logo} shouldLazyLoad css={styles.partnerImage} />
                )}
              </li>
            ) : null
          )}
        </ul>
      </div>
    ) : null;

  const imagePanel = () => (
    <>
      {imageEl}
      {associatedPartnersEl}
    </>
  );

  const descriptionEl: React.ReactNode = (
    <DataConsumerWrapper template={description}>
      {({ result, isLoading }) =>
        isLoading ? (
          <div>Loading</div>
        ) : (
          <Typography2
            variant="bodyPrimary"
            component="div"
            color="inherit"
            css={[styles.description, isHero ? styles.descriptionHero : null]}
          >
            <Markdown source={result} module={content} />
          </Typography2>
        )
      }
    </DataConsumerWrapper>
  );

  const howDetailsEl = howDetailsCollection?.items ? (
    <>
      {content.howDetailsDisplayStyle === 'two-columns-with-desc' ? (
        <ul css={styles.howDetailsTwoColumns}>
          {howDetailsCollection.items.map((howDetail) =>
            howDetail ? (
              <li>
                <Typography2 variant="subtitleMedium" component="div" css={styles.howDetailsTwoColumnsHeadline}>
                  {howDetail.headline}
                </Typography2>
                <Typography2 component="p" css={styles.howDetailsTwoColumnsDesc}>
                  {howDetail.description}
                </Typography2>
              </li>
            ) : null
          )}
        </ul>
      ) : (
        <BenefitList
          items={howDetailsCollection.items.map((item) => item?.headline)}
          style={css`
            span {
              font-size: 16px !important;
            }
          `}
        />
      )}
    </>
  ) : null;

  const secondaryCtAsEl = secondaryCtAsCollection?.items ? (
    <>
      {secondaryCtAsCollection.items.map((cta) => (
        <div css={styles.secondaryCTAs}>
          <ContentfulButton
            component="a"
            variant="ghost"
            href={cta?.ctaTarget}
            target={cta?.shouldOpenInNewWindow ? '_blank' : undefined}
            module={content}
            position={0}
            icon={<ArrowNextIcon />}
          >
            {cta?.ctaText}
          </ContentfulButton>
        </div>
      ))}
    </>
  ) : null;

  const proofPointsEl =
    content.proofPointsDisplayStyle === 'two-columns-in-block-content' &&
    proofPointsCollection?.items &&
    proofPointsCollection?.items.length > 0 ? (
      <InlineProofPoints
        proofPoints={proofPointsCollection.items as NonNullableArray<typeof proofPointsCollection.items>}
        assembly={assembly}
      />
    ) : null;

  const contentPanel = () => (
    <>
      {eyebrow && (
        <Typography2
          component="p"
          variant="bodyPrimary"
          css={styles.eyebrow(theme, eyebrowColor ?? '')}
          color="inherit"
        >
          {eyebrow}
        </Typography2>
      )}
      {visibility?.headline && headline && (
        <Typography
          data-testid="how_module_hero_heading"
          variant={isHero ? 'd1semibold' : 'd2semibold'}
          css={[styles.headline, isHero ? styles.headlineHero : null]}
          color="inherit"
        >
          {headline}
        </Typography>
      )}
      {visibility?.description && description && descriptionEl}
      {visibility?.howDetails && howDetailsCollection?.items && howDetailsEl}
      {visibility?.proofPoints && proofPointsEl}
      {visibility?.secondaryCtAs && secondaryCtAsCollection?.items && secondaryCtAsEl}

      {visibility?.ctaText && ctaText && ctaTarget && (
        <div css={[styles.ctaSection, isHero ? styles.ctaSectionHero : null]}>
          {(ctaVariant === 'primary' || ctaVariant === 'secondary' || ctaVariant === 'primaryInvert') && (
            <DataConsumerWrapper template={ctaTarget}>
              {({ result, isLoading }) =>
                isLoading ? (
                  <CDSButton variant={ctaVariant} disabled>
                    {_t('Loading...')}
                  </CDSButton>
                ) : (
                  <ContentfulButton variant={ctaVariant} module={content} href={result} position={0} component="a">
                    {ctaText}
                  </ContentfulButton>
                )
              }
            </DataConsumerWrapper>
          )}
          {(ctaVariant === 'ghost' || ctaVariant === 'ghostInvert') && (
            <div css={styles.ghostAlignment}>
              <DataConsumerWrapper template={ctaTarget}>
                {({ result, isLoading }) =>
                  isLoading ? (
                    <CDSButton variant="ghost" disabled>
                      {_t('Loading...')}
                    </CDSButton>
                  ) : (
                    <ContentfulButton
                      variant={ctaVariant as Partial<ButtonVariants>}
                      href={result}
                      module={content}
                      // target={}
                      position={0}
                      icon={<ArrowNextIcon />}
                      component="a"
                    >
                      {ctaText}
                    </ContentfulButton>
                  )
                }
              </DataConsumerWrapper>
            </div>
          )}
          {visibility?.subCtaText && subCtaText?.json && (
            <div css={styles.subCtaText}>
              <RichText
                content={subCtaText.json}
                module={content}
                typographyColor="invertBody"
                icon={<ExternalLinkIcon size="small" css={styles.externalLinkIcon} />}
              />
            </div>
          )}
          {visibility?.subCtaTextMarkdown && subCtaTextMarkdown && (
            <div css={styles.subCtaText}>
              <DataConsumerWrapper template={subCtaTextMarkdown}>
                {({ result, isLoading }) =>
                  isLoading ? (
                    <Typography2 component="p" color="inherit">
                      {_t('Loading...')}
                    </Typography2>
                  ) : (
                    <Markdown module={content} source={result} />
                  )
                }
              </DataConsumerWrapper>
            </div>
          )}
        </div>
      )}

      {visibility?.testimonial &&
        testimonialCollection?.items.map(
          (testimonial) =>
            testimonial && (
              <TestimonialModule
                content={testimonial}
                assembly={{
                  ...assembly,
                  layout: 'no-image',
                  layoutOptions: ['inner-layout'],
                  visibility: {
                    headline: true,
                    testimonial: true,
                    authorOrganization: true,
                    author: true,
                    authorTitle: true,
                  },
                }}
              />
            )
        )}
    </>
  );

  const validProofPoints =
    content.proofPointsDisplayStyle === 'black-banner-at-bottom' && proofPointsCollection?.items
      ? (proofPointsCollection.items.filter((pp) => !!pp) as NonNullableArray<EntWebsiteProofPointFragment>)
      : [];

  const hasExpandedContentColumns =
    visibility?.expandedContentColumns &&
    content?.expandedContentColumnsCollection &&
    content?.expandedContentColumnsCollection?.items?.length;

  return (
    <>
      <BlockLayout
        assembly={assembly}
        renderContent={contentPanel}
        renderImage={imagePanel}
        blockStyle={
          hasExpandedContentColumns &&
          css`
            padding-bottom: var(--cds-spacing-300);
          `
        }
      />
      {visibility?.proofPoints && validProofPoints.length > 0 && (
        <ProofPoints
          proofPoints={validProofPoints}
          heading={proofPointsModuleHeading}
          subheading={proofPointsModuleSubheading}
          backgroundColor={proofPointsModuleBackgroundColor}
          fontColor={proofPointsModuleFontColor}
          cta={proofPointsModuleCta}
          ctaButtonVariant={proofPointsModuleCtaButtonVariant}
        />
      )}
      {hasExpandedContentColumns ? (
        <BlockLayout
          assembly={noImage(assembly)}
          blockStyle={css`
            padding-top: 0;
          `}
          renderContent={() =>
            content?.expandedContentColumnsCollection && (
              <EpxandedContentColumns columns={content?.expandedContentColumnsCollection?.items} module={content} />
            )
          }
        />
      ) : null}
    </>
  );
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockHowModule = withBlockQuery<EntWebsiteHowFragment, EntWebsiteBlockHowQuery>(entWebsiteBlockHowQuery)(
  HowModule
);
